import { useMutation, useQuery } from "@apollo/client/react";
import { EDIT_OBJECTIVE_NORMAL_DEFAULTS } from "@graphql/mutations/ObjectiveNormalDefaults";
import { GET_OBJECTIVE_NORMAL_DEFAULTS } from "@graphql/queries/ObjectiveNormalDefaults";
import { useTransition, animated, config } from "@react-spring/web";
import {
  APPOINTMENT_OBJECTIVE_NORMAL_DEFAULTS,
  UNSTABLE_OBJECTIVE_DEFAULTS,
} from "@scribenote/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { FormProvider } from "react-hook-form";

import { AccountItemBlock, AccountItemLine } from "./AccountItem";
import { getSavedField } from "../../hooks/use-default-values";
import { alert } from "../common/Alert";
import { DSButton, DSToggle } from "../design-system/molecules";
import ObjectiveFieldItem from "../notes/legacy/LegacyObjectiveFieldItem";

function getObjectiveNormalDefaultsFieldArray(
  objectiveNormalDefaults,
) {
  let result = [];

  if (!objectiveNormalDefaults) {
    return [];
  }

  APPOINTMENT_OBJECTIVE_NORMAL_DEFAULTS.forEach(
    ({ name, label, type }) => {
      if (!objectiveNormalDefaults[name]) {
        // This is a new note or a new template
        result.push({
          name,
          value: "",
          label,
          type,
        });
      } else {
        result.push({
          name,
          value: getSavedField(name, objectiveNormalDefaults[name]),
          label,
          type,
        });
      }
    },
  );

  return result;
}

export function ObjectiveNormalDefaultsForm() {
  const methods = useForm();
  const { control, getValues, setValue, reset } = methods;
  const { useGptUnstableObjectiveDefaults } = useFlags();
  const { data } = useQuery(GET_OBJECTIVE_NORMAL_DEFAULTS);
  const [shouldUseOverride, setShouldUseOverride] = useState(null);
  const shouldUseFromData =
    data?.user?.objectiveNormalDefaults?.shouldUse ?? false;
  const shouldUse = shouldUseOverride ?? shouldUseFromData;

  useEffect(() => {
    reset({
      objectiveNormalDefaultsFieldArray: [
        ...getObjectiveNormalDefaultsFieldArray(
          data?.user?.objectiveNormalDefaults,
        ),
      ],
    });
  }, [data, reset]);

  const popUp = useTransition(shouldUse, {
    from: { opacity: 0, y: -50 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: -50 },
    config: config.gentle,
  });

  const [editObjectiveNormalDefaults] = useMutation(
    EDIT_OBJECTIVE_NORMAL_DEFAULTS,
    {
      refetchQueries: [{ query: GET_OBJECTIVE_NORMAL_DEFAULTS }],
    },
  );

  return (
    <FormProvider {...methods}>
      <AccountItemBlock
        title={
          <>
            <span>Custom Objective Defaults:</span>
          </>
        }
        desc={
          <>
            <span>
              <br />
              Turn this ON if you want to set custom default phrases
              for your objective section: <br />
              <br />
              1. This will convert simple phrases like 'normal,'
              'clear,' or 'looks good' to your preferred default
              phrase
              <br />
              <br />
              2. If you don't mention a particular system, it will be
              set to your preferred default phrase.
              <br />
              <br />
              For example, if you don&apos;t want Scribenote to say
              "Not Mentioned" for your cardiovascular section, but
              rather, "Strong and steady pulse, no murmurs or
              arrhythmias", you can set that here.
              <br /> <br />
              <b>IMPORTANT NOTE:</b> If you turn this ON and don't set
              any defaults, the default will be "Normal on Exam".
            </span>
            <span></span>
          </>
        }
      >
        <div className=" shadow-xs rounded-standard p-md">
          <AccountItemLine dataTestId="custom-objective-defaults-line">
            <div className="flex flex-row items-center space-x-sm">
              <span>Use Custom Objective Defaults</span>
              <Controller
                control={control}
                rules={{
                  required: false,
                }}
                render={() => (
                  <DSToggle
                    dataTestId="use-custom-objective-defaults"
                    checked={shouldUse}
                    onChange={async () => {
                      const newValue = !shouldUse;
                      setShouldUseOverride(newValue);
                      await editObjectiveNormalDefaults({
                        variables: {
                          objectiveNormalDefaultsInput: {
                            shouldUse: newValue,
                          },
                        },
                      });
                      mixpanel.track("Updated Account Setting", {
                        settingName: "Use Custom Objective Defaults",
                        settingKey:
                          "user.objectiveNormalDefaults.shouldUse",
                        settingValue: newValue,
                      });
                    }}
                    aria-label="Use Custom Objective Defaults"
                  />
                )}
                name="shouldUse"
                value={shouldUse}
              />
            </div>
          </AccountItemLine>

          {shouldUse
            ? popUp(
                (styles, item) =>
                  item && (
                    <animated.div style={styles}>
                      {APPOINTMENT_OBJECTIVE_NORMAL_DEFAULTS.map(
                        (field, index) => {
                          if (
                            UNSTABLE_OBJECTIVE_DEFAULTS.includes(
                              field.name,
                            ) &&
                            !useGptUnstableObjectiveDefaults
                          ) {
                            return null;
                          }
                          return (
                            <ObjectiveFieldItem
                              key={field.name}
                              fieldName={field.name}
                              fieldLabel={field.label}
                              formFieldValue={`objectiveNormalDefaultsFieldArray[${index}].value`}
                              control={control}
                              isForSOAPForm={false}
                              placeholder={"Normal on Exam"}
                              setValue={setValue}
                            />
                          );
                        },
                      )}

                      <div className="flex self-end py-md justify-self-end">
                        <DSButton
                          variant="primary"
                          size="sm"
                          dataTestId="save-objective-defaults-button"
                          onClick={async () => {
                            const formValues = getValues();

                            const structuredObjectiveNormalDefaults =
                              {};

                            formValues?.objectiveNormalDefaultsFieldArray.forEach(
                              (field) => {
                                structuredObjectiveNormalDefaults[
                                  field.name
                                ] = field.value;
                              },
                            );

                            await editObjectiveNormalDefaults({
                              variables: {
                                objectiveNormalDefaultsInput: {
                                  shouldUse: shouldUse,
                                  ...structuredObjectiveNormalDefaults,
                                },
                              },
                            });
                            alert(
                              "success",
                              "Custom defaults saved!",
                            );
                          }}
                        >
                          Save Defaults
                        </DSButton>
                      </div>
                    </animated.div>
                  ),
              )
            : null}
        </div>
      </AccountItemBlock>
    </FormProvider>
  );
}
